const BLANK_MARKER = "#...#";

const ZEPTO_TYPE_LABELS: Record<number, string> = {
  1: "Слово",
  2: "Предложение",
  3: "Задание",
  4: "Диалог",
  5: "Сборка",
  6: "Выбор формы",
};

export type TaskSentenceSegment = {
  kind: "text" | "blank";
  value: string;
};

export type ZeptoRowEdit = {
  index: number;
  type: number;
  rowId: number;
  arabic: string;
  russian: string;
  ruleId: string;
  alwaysShowTranslate: boolean;
  taskAnswer: string;
  taskSentence: string;
  ttsVoice: string;
  audioPath?: string;
  useJson: boolean;
  json: string;
};

export function parseSelectZeptoExtras(content: unknown): { ruleId: string; alwaysShowTranslate: boolean } {
  if (!Array.isArray(content)) {
    return { ruleId: "", alwaysShowTranslate: false };
  }
  let ruleId = "";
  let alwaysShowTranslate = false;
  for (let i = 2; i < content.length; i += 1) {
    const value = content[i];
    if (value === true) {
      alwaysShowTranslate = true;
      continue;
    }
    if (typeof value === "string" && value.trim() && !ruleId) {
      ruleId = value.trim();
    }
  }
  return { ruleId, alwaysShowTranslate };
}

function appendSelectZeptoExtras(content: unknown[], row: ZeptoRowEdit): void {
  if (row.ruleId) {
    content.push(row.ruleId);
  }
  if (row.alwaysShowTranslate) {
    content.push(true);
  }
}

function isKnownZeptoType(type: number): boolean {
  return Number.isFinite(type) && type >= 1 && type <= 6;
}

function textFromContentPart(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
    return value.join("\n");
  }
  return "";
}

function translateFromContentPart(value: unknown): string {
  if (!Array.isArray(value)) {
    return "";
  }
  const ru = value[0];
  return typeof ru === "string" ? ru : "";
}

function serializeTranslationsPart(russian: string): string[] {
  return russian.trim() ? [russian] : [];
}

function jsonFallbackRow(raw: unknown, index: number): ZeptoRowEdit {
  return {
    index,
    type: 1,
    rowId: 0,
    arabic: "",
    russian: "",
    ruleId: "",
    alwaysShowTranslate: false,
    taskAnswer: "",
    taskSentence: "",
    ttsVoice: "",
    useJson: true,
    json: JSON.stringify(raw ?? null, null, 2),
  };
}

function stripBuildMarkers(text: string): string {
  const match = text.match(/\|\*(.+)\|\|/);
  if (match) {
    return match[1]
      .split("*")
      .map((part) => part.trim())
      .filter(Boolean)
      .join(" ");
  }
  return text.replace(/\|/g, "").trim();
}

export function taskAnswerParts(answer: string): string[] {
  if (!answer) {
    return [];
  }
  return answer.split("#");
}

export function countTaskBlanks(sentence: string): number {
  if (!sentence) {
    return 0;
  }
  return sentence.split(BLANK_MARKER).length - 1;
}

export function fillTaskBlanks(sentence: string, answer: string): string {
  if (!sentence.includes(BLANK_MARKER)) {
    return sentence;
  }
  const parts = taskAnswerParts(answer);
  let partIndex = 0;
  let result = sentence;
  while (result.includes(BLANK_MARKER)) {
    const replacement =
      parts.length > 1
        ? parts[Math.min(partIndex, parts.length - 1)] ?? ""
        : answer;
    result = result.replace(BLANK_MARKER, replacement);
    partIndex += 1;
  }
  return result;
}

export function splitTaskSentence(
  sentence: string,
  selectedAnswer: string,
): TaskSentenceSegment[] {
  const parts = taskAnswerParts(selectedAnswer);
  const segments: TaskSentenceSegment[] = [];
  let rest = sentence;
  let blankIndex = 0;

  while (rest.length) {
    const pos = rest.indexOf(BLANK_MARKER);
    if (pos === -1) {
      if (rest) {
        segments.push({ kind: "text", value: rest });
      }
      break;
    }

    if (pos > 0) {
      segments.push({ kind: "text", value: rest.slice(0, pos) });
    }

    const blankValue = selectedAnswer
      ? parts.length > 1
        ? parts[Math.min(blankIndex, parts.length - 1)] ?? ""
        : selectedAnswer
      : "";

    segments.push({ kind: "blank", value: blankValue });
    blankIndex += 1;
    rest = rest.slice(pos + BLANK_MARKER.length);
  }

  return segments;
}

export function zeptoTypeLabel(type: number): string {
  return ZEPTO_TYPE_LABELS[type] ?? `Тип ${type}`;
}

export function buildEditorAudioMap(
  rows: ZeptoRowEdit[],
  prev: Record<string, string>,
  uploadedByRowId: Map<number, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const key = String(row.rowId);
    const uploaded = uploadedByRowId.get(row.rowId);
    if (uploaded) {
      out[key] = uploaded;
      continue;
    }
    if (row.audioPath) {
      out[key] = row.audioPath;
      continue;
    }
    const existing = prev[key];
    if (existing) {
      out[key] = existing;
    }
  }
  return out;
}

export function getArabicTextForTts(row: ZeptoRowEdit): string {
  if (row.useJson) {
    return "";
  }
  if (row.type === 3) {
    if (row.taskSentence && row.taskAnswer) {
      return fillTaskBlanks(row.taskSentence, row.taskAnswer);
    }
    return "";
  }
  const text = row.arabic.trim();
  if (!text) {
    return "";
  }
  if (row.type === 5) {
    return stripBuildMarkers(text);
  }
  if (row.type === 6) {
    const pipe = text.indexOf("|");
    return (pipe >= 0 ? text.slice(0, pipe) : text).trim();
  }
  return text.replace(/\n/g, " ").trim();
}

export function normalizeZepto(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw == null) {
    return [];
  }
  throw new Error("zepto must be an array");
}

function rowIdFromRaw(raw: unknown): number | null {
  if (!Array.isArray(raw) || raw.length < 3) {
    return null;
  }
  const rowId = Number(raw[2]);
  return Number.isFinite(rowId) ? rowId : null;
}

export function parseZeptoRow(
  raw: unknown,
  index: number,
  audioRef?: string,
): ZeptoRowEdit {
  if (!Array.isArray(raw) || raw.length < 3) {
    return jsonFallbackRow(raw, index);
  }

  const type = Number(raw[0]);
  const content = raw[1];
  const rowId = Number(raw[2]);

  if (!isKnownZeptoType(type) || !Number.isFinite(rowId)) {
    return jsonFallbackRow(raw, index);
  }

  const base: ZeptoRowEdit = {
    index,
    type,
    rowId,
    arabic: "",
    russian: "",
    ruleId: "",
    alwaysShowTranslate: false,
    taskAnswer: "",
    taskSentence: "",
    ttsVoice: "",
    audioPath: audioRef,
    useJson: false,
    json: "",
  };

  if (type === 3) {
    const block0 = Array.isArray(content) && Array.isArray(content[0]) ? content[0] : [];
    const taskAnswer = String(block0[0] ?? "");
    const taskSentence = String(block0[1] ?? "");
    const translations = Array.isArray(content) && Array.isArray(content[1]) ? content[1] : [];
    return {
      ...base,
      taskAnswer,
      taskSentence,
      russian: translateFromContentPart(translations),
    };
  }

  const extras = type === 6 ? parseSelectZeptoExtras(content) : null;

  return {
    ...base,
    arabic: textFromContentPart(Array.isArray(content) ? content[0] : ""),
    russian: translateFromContentPart(Array.isArray(content) ? content[1] : ""),
    ruleId: extras?.ruleId ?? "",
    alwaysShowTranslate: extras?.alwaysShowTranslate ?? false,
  };
}

export function serializeZeptoRow(row: ZeptoRowEdit): unknown {
  if (row.useJson) {
    return JSON.parse(row.json);
  }

  if (row.type === 3) {
    return [
      3,
      [[row.taskAnswer, row.taskSentence], serializeTranslationsPart(row.russian)],
      row.rowId,
    ];
  }

  const content: unknown[] = [row.arabic, serializeTranslationsPart(row.russian)];
  if (row.type === 6) {
    appendSelectZeptoExtras(content, row);
  }
  return [row.type, content, row.rowId];
}

export function zeptoToRows(
  zepto: unknown,
  audio?: Record<string, string> | null,
): ZeptoRowEdit[] {
  const list = normalizeZepto(zepto);
  const audioMap = audio ?? {};
  return list.map((item, index) => {
    const rowId = rowIdFromRaw(item);
    const path = rowId != null ? audioMap[String(rowId)] : "";
    return parseZeptoRow(item, index, path);
  });
}

export function rowsToZepto(rows: ZeptoRowEdit[]): unknown[] {
  return rows.map((row) => serializeZeptoRow(row));
}

export function createEmptyZeptoRow(index: number, rowId: number): ZeptoRowEdit {
  return {
    index,
    type: 1,
    rowId,
    arabic: "",
    russian: "",
    ruleId: "",
    alwaysShowTranslate: false,
    taskAnswer: "",
    taskSentence: "",
    ttsVoice: "",
    useJson: false,
    json: "",
  };
}

export function reindexZeptoRows(rows: ZeptoRowEdit[]): ZeptoRowEdit[] {
  return rows.map((row, index) => ({ ...row, index }));
}

export function nextZeptoRowId(rows: ZeptoRowEdit[]): number {
  let max = 0;
  for (const row of rows) {
    if (Number.isFinite(row.rowId) && row.rowId > max) {
      max = row.rowId;
    }
  }
  return max + 1;
}

export type ProofreadKind = "source" | "translation";

export type ProofreadField =
  | "title_arabic"
  | "title_russian"
  | "arabic"
  | "russian"
  | "taskAnswer"
  | "taskSentence";

export type ProofreadTranslationReference = {
  zepto_type: number | null;
  zepto_kind: string;
  arabic_raw: string;
  arabic_for_translation: string;
  arabic_prompt?: string;
  correct_arabic?: string[];
  built_arabic?: string;
  russian_structure?: string;
};

export type ProofreadItem = {
  id: string;
  rowIndex: number | null;
  field: ProofreadField;
  text: string;
  sourceText?: string;
  rowType?: number;
  exerciseKind?: string;
  translationRules?: string;
  correctAnswersArabic?: string[];
  russianHasExerciseMarkup?: boolean;
  translationReference?: ProofreadTranslationReference;
  arabicResolved?: string;
};

export type ProofreadSuggestion = {
  id: string;
  rowIndex: number | null;
  field: ProofreadField;
  current: string;
  suggested: string;
  note?: string;
};

const SOURCE_PROOFREAD_FIELDS = new Set<ProofreadField>([
  "title_arabic",
  "arabic",
  "taskAnswer",
  "taskSentence",
]);

const TRANSLATION_PROOFREAD_FIELDS = new Set<ProofreadField>([
  "title_russian",
  "russian",
]);

export function proofreadFieldsForKind(kind: ProofreadKind): Set<ProofreadField> {
  return kind === "source" ? SOURCE_PROOFREAD_FIELDS : TRANSLATION_PROOFREAD_FIELDS;
}

export function proofreadItemId(rowIndex: number | null, field: ProofreadField): string {
  return rowIndex == null ? `title:${field}` : `${rowIndex}:${field}`;
}

export function russianHasExerciseMarkup(text: string): boolean {
  if (!text.trim()) {
    return false;
  }
  return /\|[^|]*[+-][^|]*\|/.test(text) || /\|\*/.test(text) || /\|\|/.test(text);
}

export function selectCorrectAnswersFromArabic(arabic: string): string[] {
  const answers: string[] = [];
  for (const marker of arabic.matchAll(/\|([^|]+)\|/g)) {
    const markerValue = marker[1] || "";
    for (const match of markerValue.matchAll(/\+([^+-]+)/g)) {
      const word = match[1]?.trim();
      if (word) {
        answers.push(word);
      }
    }
  }
  return answers;
}

export function arabicPromptFromSelect(arabic: string): string {
  const pipe = arabic.indexOf("|");
  return (pipe >= 0 ? arabic.slice(0, pipe) : arabic).trim();
}

export function buildProofreadTranslationReference(
  row: ZeptoRowEdit | null,
  sourceArabic: string,
): ProofreadTranslationReference {
  const raw = sourceArabic.trim();
  const ref: ProofreadTranslationReference = {
    zepto_type: row?.type ?? null,
    zepto_kind: row != null ? zeptoTypeLabel(row.type) : "Заголовок",
    arabic_raw: raw,
    arabic_for_translation: raw,
  };
  if (!row) {
    return ref;
  }
  switch (row.type) {
    case 3: {
      const filled =
        row.taskSentence && row.taskAnswer
          ? fillTaskBlanks(row.taskSentence, row.taskAnswer)
          : "";
      if (filled) {
        ref.built_arabic = filled;
        ref.arabic_for_translation = filled;
        ref.russian_structure =
          "Plain Russian translation of the full Arabic sentence (blanks already filled in built_arabic).";
      }
      break;
    }
    case 5:
      ref.built_arabic = stripBuildMarkers(raw);
      ref.arabic_for_translation = ref.built_arabic;
      ref.russian_structure =
        "One natural Russian sentence for the full built sentence (built_arabic), without |*...*| markup.";
      break;
    case 6: {
      ref.arabic_prompt = arabicPromptFromSelect(raw);
      ref.correct_arabic = selectCorrectAnswersFromArabic(raw);
      ref.arabic_for_translation = ref.arabic_prompt || raw;
      if (ref.correct_arabic.length) {
        ref.russian_structure =
          "Translate arabic_prompt as a learner question, then add a natural Russian answer using the word(s) from correct_arabic (translated to Russian).";
      } else {
        ref.russian_structure = "Plain Russian translation of arabic_prompt.";
      }
      break;
    }
    default:
      ref.russian_structure = "Plain Russian translation of arabic_for_translation.";
  }
  return ref;
}

export function arabicResolvedForProofread(
  row: ZeptoRowEdit | null,
  field: ProofreadField,
  text: string,
): string {
  const trimmed = text.trim();
  if (!row) {
    return trimmed;
  }
  if (row.type === 3) {
    return trimmed;
  }
  if (field !== "arabic") {
    return trimmed;
  }
  switch (row.type) {
    case 5:
      return stripBuildMarkers(trimmed);
    case 6: {
      const prompt = arabicPromptFromSelect(trimmed);
      const correct = selectCorrectAnswersFromArabic(trimmed);
      if (correct.length) {
        return `${prompt} → ${correct.join(", ")}`;
      }
      return prompt || trimmed;
    }
    default:
      return trimmed;
  }
}

export function proofreadTranslationRules(row: ZeptoRowEdit | null, field: ProofreadField): string {
  if (field === "title_russian") {
    return "Plain Russian lesson title for learners. No Arabic and no lesson markup (|, +, -, #, *). If the current text is Arabic, replace with Russian.";
  }
  switch (row?.type) {
    case 1:
      return "Plain Russian translation of the Arabic word or phrase. No lesson markup.";
    case 2:
      return "Plain Russian translation of the Arabic sentence. No lesson markup.";
    case 3:
      return "Plain Russian translation shown under the task question. No lesson markup. If the current text is Arabic (legacy), replace with Russian.";
    case 4:
      return "Plain Russian translation of the dialog line. No lesson markup.";
    case 5:
      return [
        "Plain Russian translation of the sentence the learner builds.",
        "Arabic uses |*part1*part2*||suffix — do NOT copy |, *, or the part list into Russian.",
        "Translate the full meaning as one natural Russian sentence.",
      ].join(" ");
    case 6:
      return [
        "Plain Russian shown to the learner after the exercise.",
        "Arabic uses |...| with options like |+correct-wrong-wrong| (+ marks the correct word, options separated by -).",
        "Do NOT copy |, +, -, or hyphen-separated option lists into Russian.",
        "Translate the prompt before the first |, then state the correct answer in natural Russian.",
      ].join(" ");
    default:
      return "Plain Russian translation for learners. No Arabic exercise markup. If the current text is Arabic, replace with Russian.";
  }
}

function proofreadTranslationItemMeta(
  row: ZeptoRowEdit | null,
  field: ProofreadField,
  text: string,
  sourceText?: string,
): Pick<
  ProofreadItem,
  | "exerciseKind"
  | "translationRules"
  | "correctAnswersArabic"
  | "russianHasExerciseMarkup"
  | "translationReference"
> {
  if (field !== "title_russian" && field !== "russian") {
    return {};
  }
  const meta: Pick<
    ProofreadItem,
    | "exerciseKind"
    | "translationRules"
    | "correctAnswersArabic"
    | "russianHasExerciseMarkup"
    | "translationReference"
  > = {
    translationRules: proofreadTranslationRules(row, field),
    russianHasExerciseMarkup: russianHasExerciseMarkup(text),
  };
  if (sourceText?.trim()) {
    meta.translationReference = buildProofreadTranslationReference(row, sourceText);
    meta.correctAnswersArabic = meta.translationReference.correct_arabic;
  }
  if (row) {
    meta.exerciseKind = zeptoTypeLabel(row.type);
  } else {
    meta.exerciseKind = "Заголовок";
  }
  return meta;
}

export function collectProofreadItems(
  kind: ProofreadKind,
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
): ProofreadItem[] {
  const items: ProofreadItem[] = [];
  if (kind === "source") {
    const title = titleArabic.trim();
    if (title) {
      items.push({
        id: proofreadItemId(null, "title_arabic"),
        rowIndex: null,
        field: "title_arabic",
        text: title,
        exerciseKind: "Заголовок",
        arabicResolved: title,
      });
    }
  } else {
    const titleAr = titleArabic.trim();
    const titleRu = titleRussian.trim();
    if (titleRu) {
      items.push({
        id: proofreadItemId(null, "title_russian"),
        rowIndex: null,
        field: "title_russian",
        text: titleRu,
        sourceText: titleAr || undefined,
        ...proofreadTranslationItemMeta(null, "title_russian", titleRu, titleAr),
      });
    } else if (titleAr) {
      items.push({
        id: proofreadItemId(null, "title_russian"),
        rowIndex: null,
        field: "title_russian",
        text: "",
        sourceText: titleAr,
        ...proofreadTranslationItemMeta(null, "title_russian", "", titleAr),
      });
    }
  }

  for (const row of rows) {
    if (row.useJson) {
      continue;
    }
    if (kind === "source") {
      if (row.type === 3) {
        for (const field of ["taskAnswer", "taskSentence"] as const) {
          const text = row[field].trim();
          if (!text) {
            continue;
          }
          items.push({
            id: proofreadItemId(row.index, field),
            rowIndex: row.index,
            field,
            text,
            rowType: row.type,
            exerciseKind: zeptoTypeLabel(row.type),
            arabicResolved: arabicResolvedForProofread(row, field, text),
          });
        }
        continue;
      }
      const text = row.arabic.trim();
      if (!text) {
        continue;
      }
      items.push({
        id: proofreadItemId(row.index, "arabic"),
        rowIndex: row.index,
        field: "arabic",
        text,
        rowType: row.type,
        exerciseKind: zeptoTypeLabel(row.type),
        arabicResolved: arabicResolvedForProofread(row, "arabic", text),
      });
      continue;
    }

    if (row.type === 3) {
      const filled =
        row.taskSentence && row.taskAnswer
          ? fillTaskBlanks(row.taskSentence, row.taskAnswer)
          : "";
      const russian = row.russian.trim();
      if (russian) {
        items.push({
          id: proofreadItemId(row.index, "russian"),
          rowIndex: row.index,
          field: "russian",
          text: russian,
          sourceText: filled || undefined,
          rowType: row.type,
          ...proofreadTranslationItemMeta(row, "russian", russian, filled),
        });
      } else if (filled) {
        items.push({
          id: proofreadItemId(row.index, "russian"),
          rowIndex: row.index,
          field: "russian",
          text: "",
          sourceText: filled,
          rowType: row.type,
          ...proofreadTranslationItemMeta(row, "russian", "", filled),
        });
      }
      continue;
    }
    const arabic = row.arabic.trim();
    const russian = row.russian.trim();
    if (russian) {
      items.push({
        id: proofreadItemId(row.index, "russian"),
        rowIndex: row.index,
        field: "russian",
        text: russian,
        sourceText: arabic || undefined,
        rowType: row.type,
        ...proofreadTranslationItemMeta(row, "russian", russian, arabic),
      });
    } else if (arabic) {
      items.push({
        id: proofreadItemId(row.index, "russian"),
        rowIndex: row.index,
        field: "russian",
        text: "",
        sourceText: arabic,
        rowType: row.type,
        ...proofreadTranslationItemMeta(row, "russian", "", arabic),
      });
    }
  }

  return items;
}

export function collectAllProofreadItems(
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
): ProofreadItem[] {
  return [
    ...collectProofreadItems("source", rows, titleArabic, titleRussian),
    ...collectProofreadItems("translation", rows, titleArabic, titleRussian),
  ];
}

export function collectProofreadItemsForRow(
  rowIndex: number,
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
): ProofreadItem[] {
  return collectAllProofreadItems(rows, titleArabic, titleRussian).filter(
    (item) => item.rowIndex === rowIndex,
  );
}

export type ProofreadLessonZeptoPayload = {
  title_arabic: string;
  title_russian: string;
  zepto: unknown[];
};

export function buildProofreadLessonZeptoPayload(
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
): ProofreadLessonZeptoPayload {
  try {
    return {
      title_arabic: titleArabic,
      title_russian: titleRussian,
      zepto: rowsToZepto(rows),
    };
  } catch {
    throw new Error("Не удалось собрать zepto: проверьте JSON в строках с сырым режимом");
  }
}

export function textHasArabicScript(text: string): boolean {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text);
}

export function rowPatchForProofreadField(
  field: ProofreadField,
  value: string,
): Partial<ZeptoRowEdit> | null {
  if (field === "title_arabic" || field === "title_russian") {
    return null;
  }
  return { [field]: value } as Partial<ZeptoRowEdit>;
}

const PROOFREAD_NAV_FIELD_ORDER: ProofreadField[] = [
  "title_arabic",
  "title_russian",
  "taskAnswer",
  "taskSentence",
  "arabic",
  "russian",
];

function proofreadNavRank(suggestion: ProofreadSuggestion): number {
  if (suggestion.rowIndex == null) {
    return suggestion.field === "title_arabic" ? 0 : 1;
  }
  const fieldRank = PROOFREAD_NAV_FIELD_ORDER.indexOf(suggestion.field);
  return 100_000 + suggestion.rowIndex * 10 + (fieldRank >= 0 ? fieldRank : 99);
}

export function compareProofreadSuggestions(a: ProofreadSuggestion, b: ProofreadSuggestion): number {
  return proofreadNavRank(a) - proofreadNavRank(b);
}

export type LessonContentIssueKind = "missing_translation" | "missing_audio";

export type LessonContentIssue = {
  id: string;
  kind: LessonContentIssueKind;
  rowIndex: number | null;
  message: string;
};

export function contentIssueId(
  rowIndex: number | null,
  kind: LessonContentIssueKind,
): string {
  return rowIndex == null ? `issue:title:${kind}` : `issue:${rowIndex}:${kind}`;
}

export function collectLessonContentIssues(
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
  rowHasAudio: (row: ZeptoRowEdit) => boolean,
): LessonContentIssue[] {
  const issues: LessonContentIssue[] = [];
  const titleAr = titleArabic.trim();
  const titleRu = titleRussian.trim();
  if (titleAr && !titleRu) {
    issues.push({
      id: contentIssueId(null, "missing_translation"),
      kind: "missing_translation",
      rowIndex: null,
      message: "Нет русского заголовка",
    });
  }

  for (const row of rows) {
    if (row.useJson) {
      continue;
    }
    const arabic = row.arabic.trim();
    const russian = row.russian.trim();
    if (row.type === 3) {
      if (row.taskAnswer.trim() && row.taskSentence.trim() && !russian) {
        issues.push({
          id: contentIssueId(row.index, "missing_translation"),
          kind: "missing_translation",
          rowIndex: row.index,
          message: "Нет перевода",
        });
      }
    } else if (arabic && !russian) {
      issues.push({
        id: contentIssueId(row.index, "missing_translation"),
        kind: "missing_translation",
        rowIndex: row.index,
        message: "Нет перевода",
      });
    }
    if (getArabicTextForTts(row) && !rowHasAudio(row)) {
      issues.push({
        id: contentIssueId(row.index, "missing_audio"),
        kind: "missing_audio",
        rowIndex: row.index,
        message: "Нет аудиозаписи",
      });
    }
  }

  return issues;
}

function contentIssueNavRank(issue: LessonContentIssue): number {
  if (issue.rowIndex == null) {
    return issue.kind === "missing_translation" ? 1.5 : 99;
  }
  const base = 100_000 + issue.rowIndex * 10;
  if (issue.kind === "missing_translation") {
    return base + 5.5;
  }
  if (issue.kind === "missing_audio") {
    return base + 6.5;
  }
  return base + 99;
}

export type EditorNavScrollTarget = {
  componentId: string;
  componentIndex: string;
  rowIndex: number | null;
};

export function editorNavScrollTarget(navId: string): EditorNavScrollTarget {
  if (navId === contentIssueId(null, "missing_translation")) {
    return {
      componentId: "editor_issue_title_translation",
      componentIndex: "0",
      rowIndex: null,
    };
  }
  if (navId.startsWith("issue:")) {
    const rest = navId.slice("issue:".length);
    if (rest.startsWith("title:")) {
      return {
        componentId: "editor_issue_title_translation",
        componentIndex: "0",
        rowIndex: null,
      };
    }
    const sep = rest.indexOf(":");
    const rowStr = rest.slice(0, sep);
    const kind = rest.slice(sep + 1) as LessonContentIssueKind;
    const rowIndex = Number(rowStr);
    if (kind === "missing_audio") {
      return {
        componentId: "editor_issue_row_audio",
        componentIndex: rowStr,
        rowIndex,
      };
    }
    return {
      componentId: "editor_issue_row_translation",
      componentIndex: rowStr,
      rowIndex,
    };
  }
  if (navId.startsWith("title:")) {
    const field = navId.slice("title:".length);
    return {
      componentId: `editor_proofread_title_${field}`,
      componentIndex: "0",
      rowIndex: null,
    };
  }
  const sep = navId.indexOf(":");
  const rowStr = navId.slice(0, sep);
  const field = navId.slice(sep + 1);
  return {
    componentId: `editor_proofread_${field}`,
    componentIndex: rowStr,
    rowIndex: Number(rowStr),
  };
}

export function buildProofreadNavIds(
  suggestions: Record<string, ProofreadSuggestion>,
): string[] {
  return Object.values(suggestions)
    .sort(compareProofreadSuggestions)
    .map((item) => item.id);
}

export function buildContentIssueNavIds(
  issues: Record<string, LessonContentIssue>,
): string[] {
  return Object.values(issues)
    .sort((a, b) => contentIssueNavRank(a) - contentIssueNavRank(b) || a.id.localeCompare(b.id))
    .map((item) => item.id);
}
